import type React from 'react';
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';
import InputT from './InputT';

type NumberInputProps<T extends FieldValues> = {
  id?: string;
  field?: Path<T>;
  placeholder?: string;
  options?: RegisterOptions<T>;
  control?: Control<T>;
  parentClassName?: string;
  /**
   * Registers an emptied input as `undefined` instead of `NaN`, so an optional
   * number field can be cleared and still serialize to valid JSON.
   */
  emptyAsUndefined?: boolean;
} & React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

const parseOptionalNumber = (value: unknown): number | undefined => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }

  return Number(value);
};

const resolveValueParsing = (emptyAsUndefined?: boolean) => {
  // * react-hook-form ignores setValueAs while valueAsNumber is set, so the
  // * two options are mutually exclusive.
  if (emptyAsUndefined) {
    return { setValueAs: parseOptionalNumber };
  }

  return { valueAsNumber: true };
};

export function NumberInputT<T extends FieldValues>({
  id,
  field,
  placeholder,
  options,
  control,
  parentClassName,
  emptyAsUndefined,
  ...restParams
}: NumberInputProps<T>) {
  const { pattern, valueAsDate, min, max, ...restOptions } = options || {};
  const { className, step } = restParams;
  const valueParsing = resolveValueParsing(emptyAsUndefined);

  return (
    <InputT
      id={id}
      type='number'
      placeholder={placeholder}
      field={field}
      control={control}
      min={min?.toString()}
      max={max?.toString()}
      step={step}
      options={{ ...valueParsing, ...restOptions }}
      className={className}
      parentClassName={parentClassName}
    />
  );
}

export default NumberInputT;
